import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Input,
  Select,
  Tag,
  Progress,
  Button,
  Space,
  Popconfirm,
  message,
  Spin,
  Typography,
  Row,
  Col,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  LockOutlined,
  UnlockOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/admin';
import type { TenantListItem, TenantListParams } from '../../types/admin';
import type { ColumnsType } from 'antd/es/table';
import { COLORS } from '../../constants/colors';

const { Title, Text } = Typography;
const { Option } = Select;

// 状态标签颜色和文字
const statusConfig: Record<string, { color: string; text: string }> = {
  trial: { color: 'blue', text: '试用中' },
  active: { color: 'green', text: '活跃' },
  frozen: { color: 'orange', text: '已冻结' },
  cancelled: { color: 'red', text: '已注销' },
};

// 版本标签颜色
const planColors: Record<string, string> = {
  Free: 'default',
  Starter: 'blue',
  Professional: 'purple',
  Enterprise: 'gold',
};

// 版本中文名
const planNames: Record<string, string> = {
  Free: '免费版',
  Starter: '入门版',
  Professional: '专业版',
  Enterprise: '企业版',
};

export const TenantManagement: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState<TenantListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState<TenantListParams>({
    search: '',
    status: undefined,
    plan: undefined,
  });

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getAdminTenants({
        ...searchParams,
        page,
        page_size: pageSize,
      });
      if (res.data) {
        setTenants(res.data.items || []);
        setTotal(res.data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      message.error('获取租户列表失败');
    } finally {
      setLoading(false);
    }
  }, [searchParams, page, pageSize]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  // 处理搜索
  const handleSearch = (value: string) => {
    setSearchParams((prev) => ({ ...prev, search: value }));
    setPage(1);
  };

  // 处理状态筛选
  const handleStatusChange = (value: string | undefined) => {
    setSearchParams((prev) => ({ ...prev, status: value }));
    setPage(1);
  };

  // 处理版本筛选
  const handlePlanChange = (value: string | undefined) => {
    setSearchParams((prev) => ({ ...prev, plan: value }));
    setPage(1);
  };

  // 冻结租户
  const handleFreeze = async (id: number) => {
    try {
      await adminApi.freezeTenant(id, { reason: '管理员操作冻结' });
      message.success('租户已冻结');
      fetchTenants();
    } catch (error) {
      console.error('Failed to freeze tenant:', error);
      message.error('冻结租户失败');
    }
  };

  // 解冻租户
  const handleUnfreeze = async (id: number) => {
    try {
      await adminApi.unfreezeTenant(id);
      message.success('租户已解冻');
      fetchTenants();
    } catch (error) {
      console.error('Failed to unfreeze tenant:', error);
      message.error('解冻租户失败');
    }
  };

  // 注销租户
  const handleDelete = async (id: number) => {
    try {
      await adminApi.deleteTenant(id);
      message.success('租户已注销');
      fetchTenants();
    } catch (error) {
      console.error('Failed to delete tenant:', error);
      message.error('注销租户失败');
    }
  };

  // 查看详情
  const handleViewDetail = (id: number) => {
    navigate(`/admin/tenants/${id}`);
  };

  // 获取健康度进度条颜色
  const getHealthColor = (score: number) => {
    if (score < 40) return COLORS.error;
    if (score < 70) return COLORS.warning;
    return COLORS.success;
  };

  // 表格列定义
  const columns: ColumnsType<TenantListItem> = [
    {
      title: '租户名称',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <span style={{ fontWeight: 500 }}>{name}</span>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '版本',
      dataIndex: 'plan_name',
      key: 'plan_name',
      width: 120,
      render: (plan: string) => (
        <Tag color={planColors[plan] || 'default'}>
          {planNames[plan] || plan}
        </Tag>
      ),
    },
    {
      title: '成员数',
      dataIndex: 'member_count',
      key: 'member_count',
      width: 100,
      align: 'center',
    },
    {
      title: '健康度评分',
      dataIndex: 'health_score',
      key: 'health_score',
      width: 150,
      render: (score: number) => (
        <Progress
          percent={score}
          size="small"
          strokeColor={getHealthColor(score)}
          format={(percent) => <span style={{ fontSize: 12 }}>{percent}</span>}
        />
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time: string) =>
        time ? new Date(time).toLocaleString() : '-',
    },
    {
      title: '最后活跃',
      dataIndex: 'last_active_at',
      key: 'last_active_at',
      width: 180,
      render: (time: string) =>
        time ? new Date(time).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_: unknown, record: TenantListItem) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record.id)}
          >
            详情
          </Button>
          {record.status === 'frozen' ? (
            <Popconfirm
              title="确认解冻"
              description="确定要解冻该租户吗？"
              onConfirm={() => handleUnfreeze(record.id)}
              okText="确认"
              cancelText="取消"
            >
              <Button type="link" size="small" icon={<UnlockOutlined />}>
                解冻
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="确认冻结"
              description="确定要冻结该租户吗？冻结后租户将无法使用平台功能。"
              onConfirm={() => handleFreeze(record.id)}
              okText="确认"
              cancelText="取消"
            >
              <Button type="link" size="small" danger icon={<LockOutlined />}>
                冻结
              </Button>
            </Popconfirm>
          )}
          <Popconfirm
            title="确认注销"
            description="确定要注销该租户吗？此操作不可恢复！"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              注销
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div>
        <Title level={4} style={{ marginBottom: 24 }}>
          租户管理
        </Title>

        {/* 搜索筛选栏 */}
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col span={8}>
              <Input.Search
                placeholder="搜索租户名称"
                allowClear
                enterButton={<><SearchOutlined /> 搜索</>}
                onSearch={handleSearch}
              />
            </Col>
            <Col span={6}>
              <Select
                placeholder="筛选状态"
                allowClear
                style={{ width: '100%' }}
                onChange={handleStatusChange}
              >
                <Option value="trial">试用中</Option>
                <Option value="active">活跃</Option>
                <Option value="frozen">已冻结</Option>
                <Option value="cancelled">已注销</Option>
              </Select>
            </Col>
            <Col span={6}>
              <Select
                placeholder="筛选版本"
                allowClear
                style={{ width: '100%' }}
                onChange={handlePlanChange}
              >
                <Option value="Free">免费版</Option>
                <Option value="Starter">入门版</Option>
                <Option value="Professional">专业版</Option>
                <Option value="Enterprise">企业版</Option>
              </Select>
            </Col>
            <Col span={4} style={{ textAlign: 'right' }}>
              <Text type="secondary">共 {total} 个租户</Text>
            </Col>
          </Row>
        </Card>

        {/* 租户列表 */}
        <Card>
          <Table
            columns={columns}
            dataSource={tenants}
            rowKey="id"
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showTotal: (t) => `共 ${t} 条记录`,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
            }}
            scroll={{ x: 1200 }}
          />
        </Card>
      </div>
    </Spin>
  );
};

export default TenantManagement;
